// libraries.
import { useSelector } from 'react-redux';

// domain.
import { CountryCode } from '@domains/country';
import { getSelectedTask } from '@domains/documents-review/store/slices/document-review-slice';
import { useGetFieldRequirementsQuery } from '@domains/documents-review/store/api-slices/field-requirements.endpoints';
import { DEFAULT_FIELD_REQUIREMENTS } from '@domains/documents-review/config/fieldRequirementsDefaults';
import { mapFormFieldToSuggestionField } from '@domains/documents-review/config/fieldConfigs';

// El servicio hoy no diferencia committee vs real_estate, pero mandamos el business_type
// real de la tarea para que quede correcto cuando empiecen a divergir.
const DEFAULT_BUSINESS_TYPE = 'committee';

/**
 * useFieldRequirements — resuelve qué campos son obligatorios para un país.
 *
 * El servicio `field_requirements` es la fuente de verdad de la POLÍTICA (qué es obligatorio).
 * La APLICABILIDAD (si el campo aplica según otras respuestas) la sigue decidiendo el form vía
 * su renderizado condicional; por eso `isRequired` se evalúa solo cuando el campo está visible.
 *
 * Fallback: si el servicio no respondió (data undefined), cae a la config manual actual
 * (DEFAULT_FIELD_REQUIREMENTS) → nunca bloquea ni sub-valida al revisor.
 */
export const useFieldRequirements = (country: CountryCode) => {
  const selectedTask = useSelector(getSelectedTask);
  const businessType =
    selectedTask?.business_type?.toLowerCase() || DEFAULT_BUSINESS_TYPE;

  const { data, isLoading, isError } = useGetFieldRequirementsQuery(
    { country, businessType },
    { skip: !country }
  );

  const fallback = DEFAULT_FIELD_REQUIREMENTS[country] ?? {};

  /**
   * ¿El campo es obligatorio?
   * - Servicio disponible → él manda (campo ausente = no obligatorio).
   * - Servicio caído → config manual actual.
   *
   * El servicio nombra los campos como las sugerencias (ej. MX: `halfBathroom`), mientras
   * el form usa su propio nombre (`halfBaths`); mapeamos form→servicio para el lookup del
   * servicio. El fallback se consulta con el nombre del form (así lo define cada país).
   */
  const isRequired = (fieldName: string): boolean => {
    if (data) return data[mapFormFieldToSuggestionField(fieldName)] ?? false;
    return fallback[fieldName] ?? false;
  };

  return { isRequired, isLoading, isError };
};
